import { Inject, Injectable, OnDestroy } from '@angular/core';
import { DwOrmDataService, DwOrmDataServiceToken, DwFieldType, DwFieldAggregate, DwSecurityUserService } from '@devwareapps/devware-cap';
import { BehaviorSubject, combineLatest, defer, Observable, of, Subscription, timer, throwError } from 'rxjs';
import { catchError, debounceTime, filter, finalize, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { ChallengeContextService } from '../../shared/contexts/challenge-context/challenge-context.service';
import { AppMetaData, UserProfileEntity, UserChallengeEntity, ChallengeTeamEntity, ChallengeTeamJoinRequestEntity, ChallengeTeamJoinRequestStatusAllItems, EmailChallengeEntity, ChallengeDefinitionEntity, YearsStreakingEntity } from '../../../meta-data/app-meta-data.service';
import { CentralState, Recruiter, Section, section, initialState, lifecycle, registrationGoals, streakerYearLabel } from '../models/central.models';

import { CentralDataService } from './central-data.service';

interface RegistrationCount extends UserChallengeEntity { Total?: number; }
interface StreakerYearsCount extends UserChallengeEntity { Count?: number; }
interface SentInvitationCount extends EmailChallengeEntity { Sent?: number; }
interface AcceptedInvitationCount extends UserChallengeEntity { Accepted?: number; }

/** One instance per Central shell. Switching challenge/account cancels all prior reads. */
@Injectable()
export class StreakerCentralStateService implements OnDestroy {
    private subject = new BehaviorSubject<CentralState>(initialState());
    readonly state$ = this.subject.asObservable();
    private subscriptions = new Subscription();
    private savingInvitation = false;
    private _invitationSaveToken: object;
    private generation = 0;
    constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService,
        private security: DwSecurityUserService, selection: ChallengeContextService,
        private data: CentralDataService) {
        this.subscriptions.add(combineLatest([selection.challengeChange$,
            security.securityContextChanged().pipe(startWith(null)), data.changed$.pipe(startWith(null))])
            .pipe(switchMap(([selected]) => {
                this.generation++;
                this.subject.next(initialState());
                const context = this.security.securityContext;
                const user = context && (context.ApplicationUser || context.AuthenticatedUser);
                const userId = user && user.UserId || 0;
                return this.data.definitions().pipe(switchMap(definitions => {
                    const active = definitions.filter(c => c.IsCurrent === true);
                    const challenge = selected.ChallengeDefinitionId > 0
                        ? definitions.find(c => c.ChallengeDefinitionId === selected.ChallengeDefinitionId)
                        : active.length === 1 ? active[0] : null;
                    if (!challenge) { throw new Error('Select a valid challenge. Exactly one active challenge is required for the default selection.'); }
                    const base = { ...initialState(), challenge, userId, ...lifecycle(challenge) };
                    this.subject.next(base);

                    if (base.mode === 'unknown') { throw new Error('Challenge dates are missing or invalid.'); }
                    if (!userId) { return this.loadVisibleSections({ ...base, loading: false, membership: 'unregistered' as const }); }
                    return this.loadUserProfile(userId)
                        .pipe(switchMap(profiles => {
                            if (profiles.length > 1) { throw new Error('Multiple profiles were returned for this account.'); }
                            const profile = profiles[0];
                            if (!profile) { return this.loadVisibleSections({ ...base, loading: false, membership: 'unregistered' as const }); }
                            return this.loadEnrollment(profile.UserProfileId, challenge.ChallengeDefinitionId)
                                .pipe(switchMap(participants => {
                                    if (participants.length > 1) { throw new Error('Multiple enrollments were returned for this challenge.'); }
                                    const participant = participants[0];
                                    const state: CentralState = { ...base, loading: false, profile, participant,
                                        membership: participant && participant.IsRegistrationComplete === true ? 'registered' : 'unregistered' };
                                    this.subject.next(state);
                                    return state.membership === 'registered' || state.mode === 'after-streak' ? this.loadSections(state) : of(state);
                                }));
                        }));
                }), catchError(() => of({ ...initialState(), loading: false,
                    error: 'Unable to load Central. Check your challenge selection and account access, then retry.' })));
            })).subscribe(state => this.subject.next(state)));
        this.subscriptions.add(data.teamChanged$.pipe(debounceTime(0), switchMap(change => {
            const state = this.state, generation = this.generation;
            if (change.team && !change.removed) {
                this.subject.next({ ...state, teams: { ...state.teams, data: state.teams.data.map(team => team.ChallengeTeamId === change.teamId ? { ...team, ...change.team } : team) } });
            }
            if (!state.participant || (change.member && change.member.UserChallengeId !== state.participant.UserChallengeId)) { return of(null); }
            return this.loadParticipantTeams(state.participant.UserChallengeId, state.challenge.ChallengeDefinitionId).pipe(take(1),
                map(teams => ({ teams, generation })), catchError(() => of(null)));
        })).subscribe(result => {
            if (result && result.generation === this.generation) { this.subject.next({ ...this.state, teams: { ...this.state.teams, data: result.teams } }); }
        }));
        if (data.invitationsChanged$) {
            this.subscriptions.add(data.invitationsChanged$.pipe(debounceTime(100)).subscribe(() => {
                if (!this.state.loading && this.state.challenge && !this.savingInvitation) { this.refreshInvitationSections(); }
            }));
        }
        this.subscriptions.add(timer(60000, 60000).subscribe(() => {
            const state = this.subject.value;
            if (!state.challenge) { return; }
            const dates = lifecycle(state.challenge);
            if (dates.mode !== state.mode) { this.refresh(); }
            else { this.subject.next({ ...state, ...dates }); }
        }));
    }
    get state(): CentralState { return this.subject.value; }
    refresh(): void { this.data.invalidate(); }
    refreshInvitationSections(): void {
        const generation = this.generation;
        this.subscriptions.add(this.loadSections(this.state).pipe(
            filter(result => !result.history.loading && !result.accepted.loading && !result.leaderboard.loading && !result.emailLeaders.loading),
            take(1)).subscribe(result => {
            if (generation === this.generation) {
                this.subject.next({ ...this.state, history: result.history, accepted: result.accepted,
                    leaderboard: result.leaderboard, emailLeaders: result.emailLeaders });
            }
        }));
    }
    /** Creating this CAP record invokes the existing configured email template workflow. */
    saveInvitation(name: string, email: string): Observable<void> {
        return defer(() => {
            const state = this.state;
            const generation = this.generation;
            const address = email.trim().toLowerCase();
            const friendName = name.trim();
            if (state.mode === 'after-streak' || this.savingInvitation || state.membership !== 'registered' || !state.challenge || !state.challenge.IsCurrent || !state.userId ||
                !state.profile || !state.profile.MyChallengeCode) { return throwError(new Error('Invitations are not available in this context.')); }
            if (friendName.length > 200 || address.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
                return throwError(new Error('Enter a valid email address.'));
            }
            this.savingInvitation = true;
            const saveToken = this._invitationSaveToken = {};
            const challengeId = state.challenge.ChallengeDefinitionId, userId = state.userId;
            return this.loadInvitationHistory(challengeId, userId).pipe(take(1), switchMap(records => {
                    if (this.generation !== generation) { throw new Error('Central changed. Reopen the invitation and try again.'); }
                    if (records.some(record => (record.FriendEmail || '').trim().toLowerCase() === address)) {
                        throw new Error('An invitation record already exists for this email in this challenge. Check invitation history.');
                    }
                    const record = AppMetaData.EmailChallenge.CreateEntity();
                    record.UserId = userId;
                    record.ChallengeDefinitionId = challengeId;
                    record.FriendName = friendName;
                    record.FriendEmail = address;
                    // CAP owns email delivery, sent timestamps, registration credit and auditing.
                    return this.orm.saveEntity(record).pipe(take(1), map(() => { this.savingInvitation = false; return undefined; }));
                }), finalize(() => { if (this._invitationSaveToken === saveToken) { this.savingInvitation = false; } }));
        });
    }
    ngOnDestroy(): void { this.subscriptions.unsubscribe(); this.subject.complete(); }
    private loadUserProfile(userId: number): Observable<UserProfileEntity[]> {
        return this.data.userProfile(userId);
    }
    private loadEnrollment(profileId: number, challengeId: number): Observable<UserChallengeEntity[]> {
        return this.data.enrollment(profileId, challengeId);
    }
    private loadInvitationHistory(challengeId: number, userId: number): Observable<EmailChallengeEntity[]> {
        const query = AppMetaData.EmailChallenge.CreateQueryBuilder()
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))
            .addFilterAnd(f => f.Equal(a => a.UserId, userId))
            .addOrderByDesc(a => a.EmailSentDateTime)
            .addOrderByDesc(a => a.RegisteredDateTime)
            .addOrderByDesc(a => a.CreatedDateTime)
            .addOrderByDesc(a => a.EmailChallengeId);
        return this.orm.executeQuery<EmailChallengeEntity>(query).pipe(take(1), map(rows => rows.sort((a, b) =>
            // The UI orders by the latest applicable event, which differs from lexicographic column ordering.
            (b.EmailSentDateTime || b.RegisteredDateTime || b.CreatedDateTime || '').localeCompare(a.EmailSentDateTime || a.RegisteredDateTime || a.CreatedDateTime || ''))));
    }
    private loadParticipantTeams(participantId: number, challengeId: number): Observable<ChallengeTeamEntity[]> {
        return this.data.memberships(participantId).pipe(switchMap(members => {
            const ids = Array.from(new Set(members.map(member => member.ChallengeTeamId)));
            return ids.length ? this.data.memberTeams(challengeId, ids) : of([]);
        }));
    }
    private loadPendingTeamRequests(participantId: number): Observable<ChallengeTeamJoinRequestEntity[]> {
        return this.data.joinRequests(participantId).pipe(map(rows => rows.filter(row =>
            row.ChallengeTeamJoinRequestStatusId === ChallengeTeamJoinRequestStatusAllItems.Pending)));
    }
    private safe<T>(source: Observable<T>, fallback: T, message: string): Observable<Section<T>> {
        return source.pipe(map(data => section(data)), catchError(() => of({ data: fallback, loading: false, error: message })),
            startWith(section(fallback, true)));
    }
    private count(challengeId: number, inviterId?: number): Observable<number> {
        const query = AppMetaData.UserChallenge.CreateQueryBuilder().setupAggregateQuery()
            .addField(a => ({ FullFieldName: a.UserChallengeId.fieldNameWithItemPrefix,
                FieldAlias: 'Total', FieldType: DwFieldType.Aggregate, AggregateFunction: DwFieldAggregate.Count }))
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))
            .addFilterAnd(f => f.Equal(a => a.IsRegistrationComplete, true));
        if (inviterId) { query.addFilterAnd(f => f.Equal(a => a.ChallengedByUserId, inviterId)); }
        return this.orm.executeQuery<RegistrationCount>(query).pipe(take(1), map(rows => Number(rows[0]?.Total || 0)));
    }
    private loadStreakerYears(challenge: ChallengeDefinitionEntity): Observable<{ label: string; count: number }[]> {
        const yearsQuery = AppMetaData.UserChallenge.CreateQueryBuilder().setupAggregateQuery().addField(a => a.YearsStreakingCv)
            .addField(a => ({ FullFieldName: a.UserChallengeId.fieldNameWithItemPrefix, FieldAlias: 'Count', FieldType: DwFieldType.Aggregate, AggregateFunction: DwFieldAggregate.Count }))
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challenge.ChallengeDefinitionId))
            .addFilterAnd(f => f.Equal(a => a.IsRegistrationComplete, true))
            .addOrderBy(a => a.YearsStreakingCv);
        const lookup = AppMetaData.YearsStreaking.CreateQueryBuilder().addFields(a => [a.YearsStreakingId, a.Meaning]);
        const options = this.data.snapshot('stats-starting-years', () => this.orm.executeQuery<YearsStreakingEntity>(lookup));
        const seasonYear = Number((challenge.ChallengeStartDate || '').slice(0, 4));
        return combineLatest([this.orm.executeQuery<StreakerYearsCount>(yearsQuery), options]).pipe(take(1), map(([rows, years]) => {
            const counts = new Map<number, number>();
            rows.forEach(row => {
                const start = Number(years.find(year => year.YearsStreakingId === row.YearsStreakingCv)?.Meaning);
                const year = Number.isInteger(start) && start >= 1900 && start <= seasonYear ? start : 0;
                counts.set(year, (counts.get(year) || 0) + Number(row.Count || 0));
            });
            return Array.from(counts).sort((a, b) => b[0] - a[0]).map(([year, count]) => ({label: streakerYearLabel(year, seasonYear), count}));
        }));
    }
    private loadSentInvitationCounts(challengeId: number): Observable<SentInvitationCount[]> {
        const emailQuery = AppMetaData.EmailChallenge.CreateQueryBuilder().setupAggregateQuery().addField(a => a.UserId)
            .addField(a => ({ FullFieldName: a.EmailChallengeId.fieldNameWithItemPrefix, FieldAlias: 'Sent', FieldType: DwFieldType.Aggregate, AggregateFunction: DwFieldAggregate.Count }))
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))
            .addFilterAnd(f => f.Equal(a => a.EmailSent, true))
            .addFilterAnd(f => f.Equal(a => a.ExcludeFromStats, false))
            .setFieldSettings(settings => settings.LoadAllLookupDisplayFields = true);
        return this.orm.executeQuery<SentInvitationCount>(emailQuery).pipe(take(1));
    }
    private loadRecruiterStandings(challengeId: number): Observable<Recruiter[]> {
        const leaderboardQuery = AppMetaData.UserChallenge.CreateQueryBuilder().setupAggregateQuery().addField(a => a.ChallengedByUserId)
            .addField(a => ({ FullFieldName: a.UserChallengeId.fieldNameWithItemPrefix, FieldAlias: 'Accepted', FieldType: DwFieldType.Aggregate, AggregateFunction: DwFieldAggregate.Count }))
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))
            .addFilterAnd(f => f.Equal(a => a.IsRegistrationComplete, true))
            .addFilterAnd(f => f.GreaterThan(a => a.ChallengedByUserId, 0))
            // Accepted is the explicit aggregate result alias above, not a database attribute name.
            .addOrderByDesc(() => ({ Field: { FullFieldName: 'Accepted' }, IsAscending: false }))
            .addOrderBy(a => a.ChallengedByUserId)
            .setFieldSettings(settings => settings.LoadAllLookupDisplayFields = true);
        return this.orm.executeQuery<AcceptedInvitationCount>(leaderboardQuery).pipe(take(1), map(rows => {
            const sorted = rows;
            return sorted.map((row, index): Recruiter => ({ userId: row.ChallengedByUserId,
                name: row.ChallengedByUserIdDisplay || 'Participant', accepted: Number(row.Accepted),
                rank: sorted.findIndex(other => Number(other.Accepted) === Number(row.Accepted)) + 1 }));
        }));
    }
    private loadVisibleSections(state: CentralState): Observable<CentralState> {
        return state.mode === 'after-streak' ? this.loadSections(state) : of(state);
    }
    private loadSections(state: CentralState): Observable<CentralState> {
        const id = state.challenge.ChallengeDefinitionId;
        const years = this.loadStreakerYears(state.challenge);
        const sent = this.loadSentInvitationCounts(id);
        const teams = state.participant ? this.loadParticipantTeams(state.participant.UserChallengeId, id) : of([] as ChallengeTeamEntity[]);
        const history = state.userId ? this.loadInvitationHistory(id, state.userId) : of([] as EmailChallengeEntity[]);
        const requests = state.participant ? this.loadPendingTeamRequests(state.participant.UserChallengeId) : of([] as ChallengeTeamJoinRequestEntity[]);
        const leaderboard = this.loadRecruiterStandings(id).pipe(shareReplay({ bufferSize: 1, refCount: true }));
        const emailLeaders = combineLatest([sent, leaderboard]).pipe(map(([emails, registrations]) => {
            const users = new Map<number, { name: string; sent: number; accepted: number }>();
            registrations.forEach(row => users.set(row.userId, { name: row.name, sent: 0, accepted: row.accepted }));
            (emails || []).filter(row => row.UserId > 0).forEach(row => {
                const existing = users.get(row.UserId);
                users.set(row.UserId, { name: existing?.name || row.UserIdDisplay || 'Participant', sent: Number(row.Sent), accepted: existing?.accepted || 0 });
            });
            return Array.from(users.entries()).sort((a, b) => b[1].accepted - a[1].accepted || b[1].sent - a[1].sent || a[0] - b[0])
                .slice(0, 10).map(row => row[1]);
        }));
        // RxJS 6 tuple overloads support at most six sources; group the statistics.
        return combineLatest([
            this.safe(teams, [], 'Unable to load your teams.'), this.safe(history, [], 'Unable to load invitation history.'),
            this.safe(leaderboard, [], 'Unable to load recruiter standings.'),
            combineLatest([this.safe(state.userId ? this.count(id, state.userId) : of(0), 0, 'Unable to load accepted registrations.'),
                this.safe(this.count(id), 0, 'Unable to load registration totals.')]),
            this.safe(requests, [], 'Unable to load pending team requests.'),
            combineLatest([this.safe(years, [], 'Unable to load streaker years.'),
                this.safe(emailLeaders, [], 'Unable to load email challenge leaders.')]),
        ]).pipe(map(([teamState, historyState, boardState, [accepted, total], requestState, [yearState, emailState]]) => ({ ...state,
            emailLeaders: emailState,
            years: yearState,
            requests: requestState,
            teams: teamState, history: historyState, leaderboard: boardState, accepted, registrationCount: total,
            goals: { loading: total.loading, error: total.error,
                data: registrationGoals(id, !total.loading && !total.error ? total.data : null) },
        })));
    }
}
